import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Twitter, Mail, ExternalLink } from 'lucide-react';

const Team = () => {
  const teamMembers = [
    {
      name: 'Venkat',
      role: 'Founder',
      image: '/images/founder.png',
      bio: 'Strategic leader with 13+ years of experience in digital transformation and software architecture.'
    },
    {
      name: 'SriLaxmi',
      role: 'CEO',
      image: '/images/ceo.jpeg',
      bio: 'Driving organizational excellence through innovative digital strategies and a commitment to client-centric growth.'
    },
    {
      name: 'Harish Kodidala',
      role: 'CFO',
      image: '/images/cfo.jpeg',
      bio: 'Expert in financial planning and strategic risk management, optimizing capital allocation to fuel global expansion.'
    },
    {
      name: 'Sathish Kodidala',
      role: 'CTO',
      image: '/images/cto.jpeg',
      bio: 'Leading technical excellence by architecting scalable systems and fostering a culture of rapid innovation and research.'
    },
    {
      name: 'Divya J',
      role: 'HR Manager',
      image: '/images/hr.jpeg',
      bio: 'Championing human capital development through strategic hiring, mentorship, and a commitment to employee well-being.'
    },
    {
      name: 'Rajesh Tanguturi',
      role: 'Operations Head',
      image: '/images/operations.jpeg',
      bio: 'Streamlining operational workflows and bridge-building between technology and business goals to ensure seamless delivery.'
    }
  ];

  return (
    <div className="bg-[#f8fafc] dark:bg-[#001226] pb-24 transition-colors duration-500">
      {/* Header */}
      <section className="bg-[#4F46E5] text-white py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-[#4F46E5] font-semibold uppercase tracking-widest text-sm mb-4 block">Our Team</span>
            <h1 className="text-4xl md:text-6xl font-black mb-6">The Minds Behind the Magic</h1>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto leading-relaxed">
              We are a diverse group of designers, developers, and strategists united by a single mission: to build extraordinary digital products.
            </p>
          </motion.div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/50 to-[#4F46E5] pointer-events-none" />
      </section>

      {/* Team Grid */}
      <section className="max-w-7xl mx-auto px-4 -mt-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
           {teamMembers.map((member, i) => (
             <motion.div
               key={member.name}
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ delay: i * 0.1 }}
               className="bg-white dark:bg-[#001a35] rounded-[3rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all border border-slate-100 dark:border-white/10 group"
             >
                <div className="aspect-[5/6] overflow-hidden relative">
                   <img src={member.image} alt={member.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                   <div className="absolute inset-0 bg-gradient-to-t from-[#4F46E5]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-8">
                      <div className="flex gap-4">
                         <button className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md text-white flex items-center justify-center hover:bg-blue-600 transition-colors">
                            <Linkedin size={18} />
                         </button>
                         <button className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md text-white flex items-center justify-center hover:bg-sky-500 transition-colors">
                            <Twitter size={18} />
                         </button>
                         <button className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md text-white flex items-center justify-center hover:bg-rose-500 transition-colors">
                            <Mail size={18} />
                         </button>
                      </div>
                   </div>
                </div>
                
                <div className="p-8">
                   <h3 className="text-xl font-semibold text-[#4F46E5] dark:text-white mb-1 group-hover:text-blue-600 transition-colors">{member.name}</h3>
                   <p className="text-blue-600 dark:text-blue-400 font-semibold text-xs uppercase tracking-widest mb-4">{member.role}</p>
                   <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed italic">
                      "{member.bio}"
                   </p>
                </div>
             </motion.div>
           ))}
        </div>
      </section>

      {/* Values CTA */}
      <section className="mt-24 py-24 bg-white dark:bg-[#001a35]">
         <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
               <motion.div
                 initial={{ opacity: 0, x: -30 }}
                 whileInView={{ opacity: 1, x: 0 }}
                 viewport={{ once: true }}
               >
                  <h2 className="text-3xl md:text-4xl font-semibold text-[#4F46E5] dark:text-white mb-8 leading-tight">We're more than just colleagues; we're a community.</h2>
                  <div className="space-y-6">
                     <div className="flex gap-4">
                        <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center shrink-0">
                           <ExternalLink size={20} />
                        </div>
                        <div>
                           <h4 className="font-semibold text-[#4F46E5] dark:text-white mb-1">Constant Collaboration</h4>
                           <p className="text-sm text-slate-500 dark:text-slate-400">We believe the best ideas come from shared perspectives and teamwork.</p>
                        </div>
                     </div>
                     <div className="flex gap-4">
                        <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl flex items-center justify-center shrink-0">
                           <ExternalLink size={20} />
                        </div>
                        <div>
                           <h4 className="font-semibold text-[#4F46E5] dark:text-white mb-1">Growth Mindset</h4>
                           <p className="text-sm text-slate-500 dark:text-slate-400">Every team member is encouraged to learn new skills and push boundaries.</p>
                        </div>
                     </div>
                  </div>
               </motion.div>
               <div className="relative">
                  <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-4">
                        <div className="h-48 bg-slate-100 rounded-3xl overflow-hidden">
                           <img src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&q=80" alt="Office" className="w-full h-full object-cover" />
                        </div>
                        <div className="h-64 bg-slate-100 rounded-3xl overflow-hidden">
                           <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80" alt="Meeting" className="w-full h-full object-cover" />
                        </div>
                     </div>
                     <div className="space-y-4 pt-8">
                        <div className="h-64 bg-slate-100 rounded-3xl overflow-hidden">
                           <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80" alt="Collaboration" className="w-full h-full object-cover" />
                        </div>
                        <div className="h-48 bg-slate-100 rounded-3xl overflow-hidden">
                           <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&q=80" alt="Work" className="w-full h-full object-cover" />
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>
    </div>
  );
};

export default Team;
